'use client';

import React, { useMemo } from 'react';

import { Cms } from '@bytel/product-wall';
import { Container, Tab, TabList, Tabs as TrilogyTabs } from '@bytel/trilogy-react';

import { WallType } from '@app-types/config';
import { ProductChildFull, ProductParentFull } from '@app-types/product';

import { Characteristics, Faq, Review, Video } from '@product-detail/components';
import { getProductFAQ } from '@product-detail/helpers';

export enum TabsEnum {
    HIGHLIGHTS = 'Points forts',
    VIDEO_PRESENTATION = 'Présentation vidéo',
    CHARACTERISTICS = 'Caractéristiques',
    REVIEWS = 'Avis clients',
    FAQ = 'FAQ',
}

export type TabsProps = {
    type: WallType;
    product: ProductParentFull;
    currentProduct: ProductChildFull;
    activeTab: TabsEnum;
    setActiveTab: (tab: TabsEnum) => void;
};

export function Tabs({ type, product, currentProduct, activeTab, setActiveTab }: Readonly<TabsProps>) {
    const { gencode, name, highlightsCms, productComparatorCms, reviews, videoUrl, title } = product;
    const faq = getProductFAQ(product, currentProduct);

    const tabConfig: Record<TabsEnum, boolean> = useMemo(
        () => ({
            [TabsEnum.HIGHLIGHTS]: highlightsCms !== undefined || productComparatorCms !== undefined,
            [TabsEnum.VIDEO_PRESENTATION]: videoUrl !== undefined,
            [TabsEnum.CHARACTERISTICS]: true,
            [TabsEnum.REVIEWS]: reviews !== undefined,
            [TabsEnum.FAQ]: faq.length > 1,
        }),
        [product, faq],
    );

    const renderTabContent = useMemo(() => {
        switch (activeTab) {
            case TabsEnum.HIGHLIGHTS:
                return (
                    <div id='highlights-section' className='ignore-sticky-scroll'>
                        <Cms content={highlightsCms} />
                        <Cms content={productComparatorCms} />
                    </div>
                );
            case TabsEnum.VIDEO_PRESENTATION:
                return <Video name={name} videoUrl={videoUrl as string} />;
            case TabsEnum.CHARACTERISTICS:
                return <Characteristics type={type} product={currentProduct} />;
            case TabsEnum.REVIEWS:
                return <Review title={title} product={gencode} reviews={reviews} />;
            case TabsEnum.FAQ:
                return <Faq product={product} faq={faq} />;
            default:
                return null;
        }
    }, [activeTab]);

    return (
        <Container>
            <TrilogyTabs small>
                <TabList className='has-text-left'>
                    {Object.entries(tabConfig).map(
                        ([tabKey, isVisible]) =>
                            isVisible && (
                                <Tab
                                    label={tabKey}
                                    onClick={() => setActiveTab(tabKey as TabsEnum)}
                                    active={activeTab === tabKey}
                                    key={tabKey}
                                    id={`${tabKey}-tab`}
                                />
                            ),
                    )}
                </TabList>
            </TrilogyTabs>
            {renderTabContent}
        </Container>
    );
}
